import dayjs, { Dayjs } from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import {
  useGenerateBatchReportsMutation,
  useGetBatchReportQuery,
} from '@/modules/admin-dashboard/queries/reports-batch.ts';
import { BaseModalProps, Button, CalendarInput, ModalContentWrapper, TextInputField } from 'ui';
import { getDisabledDate, openInNewTab } from 'ui/utils';
import {
  DownloadMergedBatchReportsResponse,
  GetBatchDetailsResponse,
  ReportStatus,
} from 'compliance-api';

const validationSchema = yup.object({
  start_date: yup.string().required(),
  end_date: yup.string().required(),
  name: yup.string().min(2).max(120).required(),
});

interface GenerateBatchReportsModalProps extends BaseModalProps {
  id: number;
  batchName: string;
  autoDownloadAfterGenerated?: boolean;
}

const GenerateBatchReportsModal = ({
  id,
  open,
  onClose,
  batchName,
  autoDownloadAfterGenerated = true,
}: GenerateBatchReportsModalProps) => {
  const [createdReport, setCreatedReport] = useState<DownloadMergedBatchReportsResponse>();
  const formik = useFormik({
    initialValues: {
      start_date: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      end_date: dayjs().format('YYYY-MM-DD'),
      name: '',
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      await mutateAsync({
        id,
        start_date: values.start_date,
        end_date: values.end_date,
        name: values.name,
      });
    },
  });

  const handleOnCompleted = useCallback(
    (reportList?: GetBatchDetailsResponse): number | false => {
      if (!createdReport?.name) return false;

      const report = reportList?.reports?.find(item => item.name === createdReport?.name);

      if (!report || report?.status === ReportStatus.Generating) return 5000;

      if (report.url) {
        openInNewTab(report.url);
        // programmaticallyDownloading(report.file_storage_item_url, report.filename);
      }

      onClose();
      setCreatedReport(undefined);

      return false;
    },
    [createdReport, onClose],
  );

  useGetBatchReportQuery({
    payload: {
      id,
      page_size: 5,
      page: 1,
    },
    disabled: !createdReport,
    onCompleted: handleOnCompleted,
  });

  const handleOnSuccess = useCallback(
    (data: DownloadMergedBatchReportsResponse) => {
      if (autoDownloadAfterGenerated) {
        setCreatedReport(data);
        return;
      }
      onClose();
    },
    [autoDownloadAfterGenerated, onClose],
  );

  const { mutateAsync, isLoading } = useGenerateBatchReportsMutation({
    onSuccess: handleOnSuccess,
  });

  const { setFieldValue, values } = formik;
  useEffect(() => {
    if (batchName && values.start_date && values.end_date) {
      const startDate = dayjs(values.start_date).format('DD-MM-YYYY');
      const endDate = dayjs(values.end_date).format('DD-MM-YYYY');
      setFieldValue('name', `${batchName} ${startDate} to ${endDate}`);
    }
  }, [batchName, values.start_date, values.end_date, setFieldValue]);

  const handleChangeDates = useCallback(
    (value: [Dayjs | null, Dayjs | null] | null) => {
      if (!value || !value[0] || !value[1]) return null;

      formik.setFieldValue('start_date', value[0].format('YYYY-MM-DD'));
      formik.setFieldValue('end_date', value[1].format('YYYY-MM-DD'));
    },
    [formik],
  );

  const dateValue = useMemo<[Dayjs, Dayjs] | null>(() => {
    if (!formik.values.start_date || !formik.values.end_date) return null;
    return [dayjs(formik.values.start_date), dayjs(formik.values.end_date)];
  }, [formik.values.end_date, formik.values.start_date]);

  return (
    <ModalContentWrapper open={open} onClose={onClose} title="Generate batch">
      <FormikProvider value={formik}>
        <div className="space-y-4">
          <TextInputField name="name" label="batch name" placeholder="Name..." />
          <CalendarInput
            range
            allowClear={false}
            label="Date range"
            rangeOnChange={handleChangeDates}
            error={formik.errors.start_date || formik.errors.end_date}
            disabledDate={getDisabledDate}
            rangeValue={dateValue}
          />
          <Button
            type="submit"
            className="mt-6"
            disabled={isLoading || !!createdReport}
            loading={isLoading || !!createdReport}
            onClick={formik.handleSubmit}
          >
            Generate
          </Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default GenerateBatchReportsModal;
